import { supabase } from '../lib/supabase'
import { Deal, TransportProvider, SaleMethod } from '../types'
import { targetMarginDollars } from '../lib/marginGuide'
import { buyingFeeFor } from './salesLedgerService'

/**
 * The sale side — §5.1 and §5.3.
 *
 * Until now the platform modelled buying a car end to end and treated selling
 * one as a status change. There was no sale price, no order date, no method, no
 * transport, and the "margin warning" only ever fired at purchase, which is the
 * one moment it cannot tell you anything about the sale.
 *
 * A proposal and a sale are the same record at different confidence levels: a
 * proposal is a priced sale that has not been committed, so both live on the
 * deal rather than in a separate table that would have to be reconciled with it.
 */

export interface TransportQuote {
  provider: TransportProvider | null
  arrangedBy: 'direct_auto' | 'dealer'
  cost: number
}

export interface SaleProposalInput {
  buyerId: string | null
  salePrice: number
  saleMethod: SaleMethod
  orderDate?: string | null
  saleDate?: string | null
  deliveryAddress?: string | null
  /** 5.1 sale record: the vehicle as it leaves us, which is not how it arrived. */
  saleOdometer?: number | null
  saleRego?: string | null
  /** The BUYER's financier. Not ppsr_finance_amount, which pays out the seller's. */
  buyerFinancierName?: string | null
  buyerFinancierContact?: string | null
  buyerFinancierReference?: string | null
  buyerFinancierPayout?: number | null
  transportProviderId?: string | null
  transportArrangedBy: 'direct_auto' | 'dealer'
  transportCost: number
}

export interface MarginCheck {
  /** Sale price less buy-in, capitalised costs, transport and the buying fee. */
  netMargin: number
  /** What the pricing guide wants for a vehicle at this buy-in. */
  guideTarget: number
  belowGuide: boolean
  warning: boolean
  reason: string | null
}

/* ------------------------------------------------------------------ *
 * Transport panel (§5.1)
 * ------------------------------------------------------------------ */

export async function loadTransportProviders(): Promise<TransportProvider[]> {
  const { data, error } = await supabase
    .from('transport_providers')
    .select('*')
    .eq('active', true)
    .order('scope', { ascending: true })
    .order('name', { ascending: true })
  if (error) throw error
  return (data || []) as TransportProvider[]
}

/** Two-letter state from a free-text location like "Richmond VIC 3121". */
export function stateFromLocation(location?: string | null): string | null {
  if (!location) return null
  const m = String(location).toUpperCase().match(/\b(VIC|NSW|QLD|SA|WA|TAS|NT|ACT)\b/)
  return m ? m[1] : null
}

/**
 * Who can carry this vehicle.
 *
 * Ordered the way the spec presents the panel — the national carrier first,
 * then whoever is local to the delivery state. A carrier that covers no state
 * we recognise is still offered last rather than hidden, because the panel is
 * editable in Admin and a new entry should not silently disappear.
 */
export function providersForState(
  providers: TransportProvider[],
  state: string | null,
  /**
   * Which direction the job runs (Winnie, 15 Sep). Pricing a sale is a freight-out job, so
   * that is the default; a carrier that only does collections from sellers has no business
   * being offered as the one delivering the car to the buyer.
   *
   * A carrier with no coverage recorded is treated as 'Both', which is what the migration
   * defaults every pre-existing row to — a blank column must not silently empty a dropdown.
   */
  direction: 'FreightIn' | 'FreightOut' = 'FreightOut'
): TransportProvider[] {
  const covers = (p: TransportProvider) =>
    !state || (p.states || []).map((s) => s.toUpperCase()).includes(state)
  const doesDirection = (p: TransportProvider) => {
    const c = p.coverage || 'Both'
    return c === 'Both' || c === direction
  }

  const eligible = providers.filter(doesDirection)
  const national = eligible.filter((p) => p.scope === 'national' && covers(p))
  const local = eligible.filter((p) => p.scope === 'local' && covers(p))
  const rest = eligible.filter((p) => !national.includes(p) && !local.includes(p))
  return [...national, ...local, ...rest]
}

/* ------------------------------------------------------------------ *
 * Margin (§5.1 "alert if margin falls below threshold")
 * ------------------------------------------------------------------ */

/**
 * Check the margin on a proposed sale.
 *
 * Deliberately measured after transport and the buying fee, because a sale that
 * looks fine on the sticker and loses money once it has been freighted across
 * the country is exactly the case the warning exists to catch. Where the buyer
 * arranges their own transport there is no cost to add.
 */
export function checkSaleMargin(
  deal: Pick<Deal, 'valuation_amount' | 'capitalised_transport' | 'capitalised_repairs'>,
  salePrice: number,
  transportCost: number,
  arrangedBy: 'direct_auto' | 'dealer'
): MarginCheck {
  const buyIn = Number(deal.valuation_amount || 0)
  const capitalised =
    Number(deal.capitalised_transport || 0) + Number(deal.capitalised_repairs || 0)
  const transport = arrangedBy === 'dealer' ? 0 : Number(transportCost || 0)

  const beforeFee = Number(salePrice || 0) - buyIn - capitalised - transport
  const netMargin = beforeFee - buyingFeeFor(beforeFee)

  const guideTarget = targetMarginDollars(buyIn)
  const belowGuide = netMargin < guideTarget

  let reason: string | null = null
  if (netMargin < 0) {
    reason = `This sale loses ${money(Math.abs(netMargin))} after costs.`
  } else if (belowGuide) {
    reason = `Net margin of ${money(netMargin)} is under the ${money(guideTarget)} the pricing guide wants at this buy-in.`
  }

  return { netMargin, guideTarget, belowGuide, warning: reason !== null, reason }
}

const money = (n: number) =>
  `$${Math.round(Number(n) || 0).toLocaleString('en-AU')}`

/* ------------------------------------------------------------------ *
 * Proposal and sale
 * ------------------------------------------------------------------ */

/**
 * Save a sale proposal against the deal. Does not move the status — a proposal
 * is a price on paper, and §5.1 has it sitting alongside the vehicle until
 * someone converts it.
 */
export async function saveProposal(
  deal: Deal,
  input: SaleProposalInput,
  userId?: string
): Promise<MarginCheck> {
  const margin = checkSaleMargin(
    deal,
    input.salePrice,
    input.transportCost,
    input.transportArrangedBy
  )

  const payload: Record<string, any> = {
    buyer_id: input.buyerId,
    sale_price: input.salePrice,
    sale_method: input.saleMethod,
    order_date: input.orderDate || null,
    sale_date: input.saleDate || null,
    delivery_address: input.deliveryAddress || null,
    sale_odometer: input.saleOdometer ?? null,
    sale_rego: input.saleRego || null,
    buyer_financier_name: input.buyerFinancierName || null,
    buyer_financier_contact: input.buyerFinancierContact || null,
    buyer_financier_reference: input.buyerFinancierReference || null,
    buyer_financier_payout: input.buyerFinancierPayout ?? null,
    transport_provider_id:
      input.transportArrangedBy === 'dealer' ? null : input.transportProviderId || null,
    transport_arranged_by: input.transportArrangedBy,
    transport_cost: input.transportArrangedBy === 'dealer' ? 0 : input.transportCost || 0,
    transport_quoted_at: input.transportProviderId ? new Date().toISOString() : null,
    sale_margin_warning: margin.warning,
    sale_margin_warning_reason: margin.reason,
    updated_at: new Date().toISOString(),
  }

  if (!deal.proposal_created_at) {
    payload.proposal_created_at = new Date().toISOString()
    payload.proposal_created_by = userId || null
  }

  const { error } = await supabase.from('deals').update(payload).eq('id', deal.id)
  if (error) throw error

  // Keep the buyer's copy of the delivery address current — the next sale to
  // the same dealership should not have to re-type it.
  if (input.buyerId && input.deliveryAddress) {
    await supabase
      .from('buyers')
      .update({ delivery_address: input.deliveryAddress, updated_at: new Date().toISOString() })
      .eq('id', input.buyerId)
  }

  return margin
}

/**
 * Copy the buyer record onto the deal.
 *
 * The deal's own buyer_* columns are what the invoice, the AR ladder and every
 * existing report read. Keeping them in step means none of that has to change,
 * and a later edit to the buyer record cannot silently restate a sale that has
 * already been invoiced.
 */
export async function attachBuyerToDeal(dealId: string, buyerId: string): Promise<void> {
  const { data: buyer, error } = await supabase
    .from('buyers')
    .select('*')
    .eq('id', buyerId)
    .single()
  if (error) throw error

  const { error: err } = await supabase
    .from('deals')
    .update({
      buyer_id: buyerId,
      buyer_name: buyer.name,
      buyer_company: buyer.trading_name || buyer.legal_name || null,
      buyer_email: buyer.email,
      buyer_mobile: buyer.mobile,
      buyer_abn: buyer.abn,
      buyer_lmct: buyer.lmct,
      buyer_address: buyer.billing_address,
      buyer_captured_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    })
    .eq('id', dealId)
  if (err) throw err
}

/* ------------------------------------------------------------------ *
 * §5.3 — issuing the tax invoice
 * ------------------------------------------------------------------ */

export interface InvoiceGate {
  ok: boolean
  problems: string[]
}

/**
 * What has to be true before a tax invoice can be issued.
 *
 * The LMCT/MDL licence is compulsory on a dealer tax invoice — it was captured
 * but never required, so an invoice could be issued without it and only fail
 * when someone downstream noticed.
 */
export function checkInvoiceReadiness(
  deal: Deal,
  buyer: { name?: string | null; abn?: string | null; lmct?: string | null; email?: string | null; credit_status?: string } | null
): InvoiceGate {
  const problems: string[] = []
  const b = buyer || {}

  if (!deal.sale_price || Number(deal.sale_price) <= 0) {
    problems.push('The sale price has not been set.')
  }
  if (!b.name) problems.push('No buyer is attached to this sale.')
  if (!b.email) problems.push('The buyer has no email address, so the invoice cannot be sent.')
  if (!b.lmct) {
    problems.push('The buyer has no LMCT/MDL licence number — it is compulsory on a wholesale tax invoice.')
  }
  if (!b.abn) problems.push('The buyer has no ABN.')
  if (b.credit_status === 'frozen' || b.credit_status === 'collections') {
    problems.push(
      `This buyer's account is ${b.credit_status === 'frozen' ? 'frozen' : 'with collections'}. Clear the overdue balance before invoicing another vehicle.`
    )
  }

  return { ok: problems.length === 0, problems }
}

/**
 * Lock the commission for a deal (§5.3, §6.1).
 *
 * Commission was recomputed from live data on every page load, so adding a cost
 * three months later quietly restated what someone had already been paid. The
 * lock records the net profit the commission was calculated against and the
 * dollar amount per attribution, at the moment the invoice is issued.
 */
export async function lockCommission(dealId: string): Promise<number> {
  const { data: deal, error } = await supabase
    .from('deals')
    .select('valuation_amount, capitalised_transport, capitalised_repairs, transport_cost, transport_arranged_by, sale_price, buying_fee, commission_locked_at')
    .eq('id', dealId)
    .single()
  if (error) throw error
  if ((deal as any).commission_locked_at) return Number((deal as any).commission_locked_net) || 0

  const d = deal as any
  const buyIn = Number(d.valuation_amount || 0)
  const capitalised = Number(d.capitalised_transport || 0) + Number(d.capitalised_repairs || 0)
  const transport = d.transport_arranged_by === 'dealer' ? 0 : Number(d.transport_cost || 0)
  const beforeFee = Number(d.sale_price || 0) - buyIn - capitalised - transport
  const fee = d.buying_fee != null ? Number(d.buying_fee) : buyingFeeFor(beforeFee)
  const net = Math.round((beforeFee - fee) * 100) / 100

  const now = new Date().toISOString()
  await supabase
    .from('deals')
    .update({ commission_locked_at: now, commission_locked_net: net, updated_at: now })
    .eq('id', dealId)

  const { data: attributions } = await supabase
    .from('deal_attributions')
    .select('id, rate')
    .eq('deal_id', dealId)

  for (const a of attributions || []) {
    await supabase
      .from('deal_attributions')
      .update({
        locked_commission: Math.round(net * Number((a as any).rate || 0) * 100) / 100,
        locked_at: now,
      })
      .eq('id', (a as any).id)
  }

  return net
}

/**
 * Recalculate a locked commission.
 *
 * §6.1 allows exactly one reason to re-open it: an expense added before the AR
 * invoice is paid. Once the money is in, the figure stands.
 */
export async function recalculateLockedCommission(dealId: string): Promise<{ ok: boolean; message: string }> {
  const { data: invoices } = await supabase
    .from('invoices')
    .select('status, payment_received_at')
    .eq('deal_id', dealId)

  const settled = (invoices || []).some(
    (i: any) => i.status === 'paid' || i.payment_received_at
  )
  if (settled) {
    return {
      ok: false,
      message: 'This invoice has been paid, so the commission is final and cannot be restated.',
    }
  }

  await supabase
    .from('deals')
    .update({ commission_locked_at: null, commission_locked_net: null })
    .eq('id', dealId)
  const net = await lockCommission(dealId)

  return { ok: true, message: `Commission re-locked against a net profit of ${money(net)}.` }
}
